import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  searchDavidsonBySirenQuery,
  searchUsersByIds,
  searchGeneralByProfileQuery,
  getNamesByFiltersQuery,
} from "./queries";
import {
  SIREN_ALREADY_EXISTS,
  SIREN_NOT_FOUND,
  SIREN_REQUEST_ERROR,
} from "constants/popup-codes";
import { useUserContext } from "api/user/UserProvider";
import { withUserScopeCheck } from "utils/helper";
import uniqBy from "lodash/uniqBy";

const GeneralContext = createContext({});

export const useGeneralContext = () => useContext(GeneralContext);

export const GeneralProvider = ({ ...props }) => {
  const [usersByIds, setUsersByIds] = useState([]);
  const [usersByProfiles, setUsersByProfiles] = useState([]);
  const searchRequestIdRef = useRef(0);
  const [sirenData, setSirenData] = useState({});
  const [sirenErrorMessage, setSirenErrorMessage] = useState("");
  const [namesByFilters, setNamesByFilters] = useState([]);
  const { userScope } = useUserContext();

  const getNamesByFilters = useCallback(
    async ({
      types,
      slug,
      names,
      state,
      id,
      profile,
      manager_of_id,
      search,
      role,
      include_deleted,
      assigned_branches_include_contract,
      allowed_scope = userScope,
    }) => {
      try {
        const result = await getNamesByFiltersQuery({
          types,
          slug,
          names,
          state,
          id,
          profile,
          manager_of_id,
          search,
          role,
          include_deleted,
          assigned_branches_include_contract,
          allowed_scope,
        });
        setNamesByFilters((prev) => uniqBy([...prev, ...result], "id"));
        return result;
      } catch (e) {
        console.error("getNamesByFilters", e);
        throw e;
      }
    },
    [userScope]
  );

  const searchUsers = useCallback(
    async ({ ids }) => {
      try {
        // Filter ids that are not already in the cache
        const existingIds = new Set(usersByIds.map((user) => user.id));
        const newIds = ids && ids?.filter((id) => !existingIds?.has(id));

        // if all ids are already in the cache, do not make an api call
        if (newIds.length === 0) {
          return;
        }
        await withUserScopeCheck({
          callback: async () => {
            const result = await searchUsersByIds({
              ids: newIds.join(","),
              userScope: userScope,
            });
            if (result) {
              setUsersByIds((old) => [...old, ...result]);
            }
          },
          scope: userScope,
        });
      } catch (e) {
        console.error("searchUsers", e);
      }
    },
    [setUsersByIds, userScope, usersByIds]
  );

  const searchGeneralByProfiles = useCallback(
    async ({ profile, search, isPartnerConnection = false }) => {
      const requestId = ++searchRequestIdRef.current;
      setUsersByProfiles([]);
      try {
        await withUserScopeCheck({
          callback: async () => {
            const result = await searchGeneralByProfileQuery({
              profile,
              search,
              userScope,
              isPartnerConnection,
            });
            if (requestId === searchRequestIdRef.current) {
              setUsersByProfiles(result);
            }
          },
          scope: userScope,
        });
      } catch (e) {
        console.error("searchGeneralByProfiles", e);
      }
    },
    [setUsersByProfiles, userScope]
  );

  const searchDavidsonBySiren = useCallback(
    async (siren, entity) => {
      try {
        await withUserScopeCheck({
          callback: async () => {
            setSirenData(
              await searchDavidsonBySirenQuery({
                siren,
                entity,
                userScope: userScope,
              })
            );
          },
          scope: userScope,
        });
      } catch (e) {
        const {
          "internal-server-error": InternalServerError,
          "bad-request": BadRequest,
          "err_type": ErrorType,
        } = e.response.data || {};
        const errorMapping = {
          "A subcontractor company already exists with this siren":
            SIREN_ALREADY_EXISTS,
          "A billing client already exists with this siren":
            SIREN_ALREADY_EXISTS,
          "NotFound": SIREN_NOT_FOUND,
          "Something happened while querying societe.com": SIREN_REQUEST_ERROR,
        };
        const sirenError = InternalServerError || BadRequest || ErrorType;
        sirenError &&
          errorMapping[sirenError] &&
          setSirenErrorMessage(errorMapping[sirenError]);
      }
    },
    [sirenData, userScope]
  );

  const values = useMemo(
    () => ({
      searchUsers,
      searchGeneralByProfiles,
      searchDavidsonBySiren,
      usersByIds,
      usersByProfiles,
      sirenData,
      setSirenData,
      sirenErrorMessage,
      setSirenErrorMessage,
      getNamesByFilters,
      namesByFilters,
      setNamesByFilters,
    }),
    [
      searchUsers,
      searchGeneralByProfiles,
      searchDavidsonBySiren,
      usersByIds,
      usersByProfiles,
      sirenData,
      setSirenData,
      sirenErrorMessage,
      setSirenErrorMessage,
      getNamesByFilters,
      namesByFilters,
      setNamesByFilters,
    ]
  );
  return <GeneralContext.Provider value={values} {...props} />;
};
